import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';

// Talent already on the platform, shown while a project sits in review.
//
// The wait is otherwise dead air: a producer submits a brief and gets a card
// saying "within 24 hours". This puts the roster in front of them instead, so the
// thing they just signed up for is visible before it is usable.
//
// It does not claim to match the brief. Of 92,790 profiles, 6% carry skills and
// 28% carry an age range, so a "matched to your project" badge would be theatre.
// It filters to profiles with a headshot, lets them narrow by area, and searches
// by name. What it shows is what a casting page shows — no contact details.

type Talent = {
  id: string;
  name: string | null;
  photo: string | null;
  ageMin: number | null;
  ageMax: number | null;
  height: string | null;
  gender: string | null;
  skills: string[];
};

const AREAS: { label: string; zip: string }[] = [
  { label: 'Anywhere', zip: '' },
  { label: 'Houston', zip: '77' },
  { label: 'Dallas', zip: '75' },
  { label: 'Austin / San Antonio', zip: '78' },
  { label: 'Los Angeles', zip: '90' },
  { label: 'New York', zip: '10' },
];

const TalentPeek: React.FC<{ defaultZip?: string }> = ({ defaultZip = '' }) => {
  const [q, setQ] = useState('');
  const [zip, setZip] = useState(defaultZip);
  const [talent, setTalent] = useState<Talent[]>([]);
  const [loading, setLoading] = useState(true);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    // A name search fires per keystroke otherwise, against 92k profiles.
    debounce.current = setTimeout(() => {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const params = new URLSearchParams({ limit: '18', ...(q ? { q } : {}), ...(zip ? { near: zip } : {}) });
      fetch(`/api/find-talent/talent?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then(r => r.json())
        .then(d => setTalent(Array.isArray(d.talent) ? d.talent : []))
        .catch(() => setTalent([]))
        .finally(() => setLoading(false));
    }, q ? 320 : 0);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [q, zip]);

  const ageLabel = (t: Talent) =>
    t.ageMin && t.ageMax ? `${t.ageMin}–${t.ageMax}` : t.ageMin ? `${t.ageMin}+` : null;

  return (
    <div style={{ marginTop: 34, textAlign: 'left' }}>
      <div className="mono-label" style={{ color: 'var(--hj-ink-3)', letterSpacing: '0.12em', marginBottom: 10 }}>
        While you wait
      </div>
      <h3 className="font-hj-display m-0" style={{ fontSize: 22, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
        Who&rsquo;s already on Hey Joe.
      </h3>
      <p className="m-0" style={{ marginTop: 8, fontSize: 14, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
        Search the roster by name, or narrow to your area. Submissions come to you once your
        project is approved.
      </p>

      <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 16 }}>
        <div
          className="flex items-center gap-2"
          style={{ flex: '1 1 220px', padding: '0 14px', minHeight: 44, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}
        >
          <Search size={15} style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search talent by name"
            aria-label="Search talent by name"
            style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', fontSize: 14.5, color: 'var(--hj-ink)', fontFamily: 'inherit' }}
          />
        </div>
        <select
          value={zip}
          onChange={e => setZip(e.target.value)}
          aria-label="Area"
          style={{ minHeight: 44, padding: '0 14px', borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', fontSize: 14.5, color: 'var(--hj-ink)', fontFamily: 'inherit' }}
        >
          {AREAS.map(a => <option key={a.zip} value={a.zip}>{a.label}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="m-0" style={{ marginTop: 18, fontSize: 14, color: 'var(--hj-ink-3)' }}>Loading…</p>
      ) : talent.length === 0 ? (
        <p className="m-0" style={{ marginTop: 18, fontSize: 14, color: 'var(--hj-ink-3)' }}>
          {q ? `Nobody matching “${q}” with a headshot up yet.` : 'No profiles to show here yet.'}
        </p>
      ) : (
        <div
          style={{
            marginTop: 18, display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
          }}
        >
          {talent.map(t => (
            <div key={t.id} style={{ borderRadius: 'var(--hj-r-sm)', overflow: 'hidden', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}>
              <img
                src={t.photo || ''}
                alt={t.name || 'Talent'}
                loading="lazy"
                style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', display: 'block', background: 'var(--hj-surface-1)' }}
              />
              <div style={{ padding: '9px 10px 11px' }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-ink)', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--hj-ink-3)', marginTop: 2 }}>
                  {[ageLabel(t), t.height].filter(Boolean).join(' · ') || ' '}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TalentPeek;
